import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router';
import { BookmarkParentType } from '~/apollo/generated/v3/graphql';
import type { Study, StudyPartsFragment } from '~/apollo/generated/v4/graphql';
import { Panel } from '~/components/common/Panel';
import type { SupportingObject } from '~/components/supporting-object/so-item-display-v4';
import { SOItemListV4 } from '~/components/supporting-object/so-item-list-v4';
import { studyRoute } from '~/paths';
import type { RefetchQueries } from '~/utils/graphql';

type Props<Key extends keyof Study, SO extends SupportingObject> = {
  outcropId: number;
  studies: Array<
    StudyPartsFragment & {
      [key in Key]: SO[];
    }
  >;
  supportObjectKey: Key;
  fade?: boolean;
  refetchQueries: RefetchQueries;
};

/** Returns a supporting object list component, but with additional study metadata */
export function TaggedSOItemListV4<
  Key extends keyof Study,
  SO extends SupportingObject,
>({
  outcropId,
  studies,
  supportObjectKey,
  fade = false,
  refetchQueries,
}: Props<Key, SO>) {
  const getSuppObjs = (study: (typeof studies)[number]) => {
    const allSOs = study[supportObjectKey] ?? [];
    return allSOs.filter(so => so.outcropTagId === outcropId);
  };

  function authorLine(study: StudyPartsFragment) {
    if (!study.collectedBy && !study.collectedYear) {
      return null;
    }

    return (
      <div className="space-x-2">
        {study.collectedBy && <span>{study.collectedBy}</span>}
        {study.collectedYear && (
          <span className="text-muted">{study.collectedYear}</span>
        )}
      </div>
    );
  }

  function bookmarkParentType(): BookmarkParentType | undefined {
    switch (supportObjectKey) {
      case 'facies':
        return BookmarkParentType.Facies;
      case 'crossSections':
        return BookmarkParentType.CrossSection;
      case 'sedimentaryLogs':
        return BookmarkParentType.SedimentaryLog;
      case 'wellLogs':
        return BookmarkParentType.WellLog;
      case 'production':
        return BookmarkParentType.Production;
      case 'reservoirModels':
        return BookmarkParentType.ReservoirModel;
      case 'trainingImages':
        return BookmarkParentType.TrainingImage;
      case 'variograms':
        return BookmarkParentType.Variogram;
      case 'gigaPans':
        return BookmarkParentType.GigaPan;
      case 'syntheticWellLogs':
        // return BookmarkParentType.SyntheticWellLog;
        return undefined;
      default:
        throw new Error(`Unhandled bookmarkable type ${supportObjectKey}`);
    }
  }

  return (
    <div className="space-y-4">
      {studies.map(study => {
        const suppObjs = getSuppObjs(study);
        if (!suppObjs.length) return null;

        return (
          <Panel key={study.id}>
            <Panel.Heading className="flex justify-between items-start">
              <Panel.Title>
                <strong>{study.name}</strong>
                {authorLine(study)}
              </Panel.Title>

              <Link
                to={studyRoute(parseInt(study.id))}
                target="_blank"
                className="btn btn-ghost btn-xs gap-1"
              >
                <FontAwesomeIcon icon={faMagnifyingGlass} /> View Study
              </Link>
            </Panel.Heading>
            <Panel.Body>
              <SOItemListV4
                items={suppObjs}
                fade={fade}
                soParentType="study"
                soParentId={parseInt(study.id)}
                bookmarkParentType={bookmarkParentType()}
                refetchQueries={refetchQueries}
              />
            </Panel.Body>
          </Panel>
        );
      })}
    </div>
  );
}
